import React, { useState, useCallback } from 'react';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { Tab } from '@components/atoms/Tab';
import { TransactionHistoryTable } from '@components/organisms/TransactionHistoryTable';
import { EquipmentTransactionHistoryTable } from '@components/organisms/EquipmentTransactionHistoryTable';
import { WarehouseLayout } from '@components/layout/WarehouseLayout';
import { useWarehouseContext } from '@components/layout/WarehouseContext';
import {
  useStockTransactions,
  useMaterialCatalog,
  useMaterialCategories,
  useAllEquipmentUsageHistory,
} from '@hooks/api/warehouse';
import { CategoryType } from '@models/warehouse.model';
import { generateTransactionHistoryPdf } from '@utils/transactionHistoryPdf';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

type TransactionTab = 'materials' | 'equipment';

const TransactionsPageContent: React.FC = () => {
  const tActionMessages = useTranslation('actionMessages');
  const { selectedWarehouse } = useWarehouseContext();
  const [activeTransactionTab, setActiveTransactionTab] = useState<TransactionTab>('materials');

  // API Hooks - Materials
  const {
    data: transactions = [],
    isLoading: transactionsLoading,
    isError: transactionsError,
    refetch: refetchTransactions,
  } = useStockTransactions({ warehouseId: selectedWarehouse?.warehouseId, limit: 500 });

  const {
    data: materials = [],
    isLoading: materialsLoading,
    isError: materialsError,
  } = useMaterialCatalog();

  // API Hooks - Categories (all)
  const {
    data: allCategories = [],
  } = useMaterialCategories();

  // Filter categories by type for each tab
  const materialCategories = allCategories.filter(
    (c) => c.type === CategoryType.MATERIAL || c.type === CategoryType.BOTH,
  );
  const equipmentCategories = allCategories.filter(
    (c) => c.type === CategoryType.EQUIPMENT || c.type === CategoryType.BOTH,
  );

  // API Hooks - Equipment Usage
  const {
    data: equipmentUsageHistory = [],
    isLoading: equipmentUsageLoading,
    isError: equipmentUsageError,
    refetch: refetchEquipmentUsage,
  } = useAllEquipmentUsageHistory({ warehouseId: selectedWarehouse?.warehouseId, limit: 500 });

  // Loading states
  const isMaterialLoading = (transactionsLoading && !transactionsError) || (materialsLoading && !materialsError);
  const isEquipmentLoading = equipmentUsageLoading && !equipmentUsageError;

  // Handlers
  const handleRefresh = useCallback(() => {
    if (activeTransactionTab === 'materials') {
      refetchTransactions();
    } else {
      refetchEquipmentUsage();
    }
  }, [activeTransactionTab, refetchTransactions, refetchEquipmentUsage]);

  const handleExportPdf = useCallback(() => {
    try {
      generateTransactionHistoryPdf({
        transactions,
        materials,
      });
      toast.success(tActionMessages.transactionHistoryExportedSuccessfully);
    } catch (error) {
      console.error('Error exporting transaction history:', error);
      toast.error(tActionMessages.failedToExportTransactionHistory);
    }
  }, [
    transactions,
    materials,
    tActionMessages.transactionHistoryExportedSuccessfully,
    tActionMessages.failedToExportTransactionHistory,
  ]);

  // Show initial loading only for the active tab
  const showInitialLoading =
    (activeTransactionTab === 'materials' && transactionsLoading && !transactionsError) ||
    (activeTransactionTab === 'equipment' && equipmentUsageLoading && !equipmentUsageError);

  if (showInitialLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loading size="3xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Transaction History
          </h1>
          <p className="text-sm text-gray-500">
            View all inventory movements and changes
          </p>
        </div>

        <div className="flex gap-2">
          {activeTransactionTab === 'materials' && (
            <Button
              variant="secondary"
              leftIcon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
              onClick={handleExportPdf}
            >
              Export PDF
            </Button>
          )}
          <Button variant="tertiary" onClick={handleRefresh}>
            <RefreshIcon sx={{ fontSize: 16 }} />
          </Button>
        </div>
      </div>

      {/* Top-level Tabs: Material vs Equipment */}
      <Tab
        tabs={[
          { id: 'materials', name: 'Material Transactions' },
          { id: 'equipment', name: 'Equipment Transactions' },
        ]}
        activeTab={activeTransactionTab}
        onTabChange={(tabId) => setActiveTransactionTab(tabId as TransactionTab)}
        variant="pills"
        size="md"
      />

      {/* Content based on active tab */}
      {activeTransactionTab === 'materials' ? (
        <TransactionHistoryTable
          transactions={transactions}
          materials={materials}
          categories={materialCategories}
          isLoading={isMaterialLoading}
        />
      ) : (
        <EquipmentTransactionHistoryTable
          usageHistory={equipmentUsageHistory}
          categories={equipmentCategories}
          isLoading={isEquipmentLoading}
        />
      )}
    </div>
  );
};

const TransactionsPage: React.FC = () => {
  return (
    <WarehouseLayout>
      <TransactionsPageContent />
    </WarehouseLayout>
  );
};

export default TransactionsPage;
